import { useCallback, useContext, useEffect, useState } from "react";

import { MapContext } from "../MapProvider";

/**
 * Custom hook to check if a toolbar tool is enabled based on the currently selected features.
 * When features are selected, returns false if ANY selected feature belongs to a layer
 * that lists the tool in its `disabledTools` array. When no features are selected, checks
 * the current editing layer instead.
 *
 * This is a generic mechanism — any toolbar button can call `useToolEnabled("toolName")`
 * and any layer can list tools to disable via `disabledTools: ["split", "cut", ...]`.
 *
 * @param toolName - The tool name to check (e.g., "split", "cut", "union", "combine")
 * @returns Boolean indicating if the tool is enabled for the current selection/layer
 */
export function useToolEnabled(toolName: string): boolean {
  const { map, drawControl } = useContext(MapContext);
  const [isEnabled, setIsEnabled] = useState(true);

  const checkEnabled = useCallback(() => {
    const allLayers = drawControl.current?.options?.editOptions?.allLayers;
    if (!allLayers || allLayers.length === 0) {
      setIsEnabled(true);
      return;
    }

    // When features are selected, check their layers
    const selectedFeatures = drawControl.current?.getSelected()?.features || [];
    if (selectedFeatures.length > 0) {
      // Disable if ANY selected feature is from a layer that disables this tool
      const anyDisabled = selectedFeatures.some((feature) => {
        const layerId = feature.properties?.layerId;
        const layer = allLayers.find((l: { ID: string }) => l.ID === layerId);
        return layer?.disabledTools?.includes(toolName);
      });
      setIsEnabled(!anyDisabled);
      return;
    }

    // No features selected — fall back to the current editing layer
    const currentEditingLayerId = drawControl.current?.options?.editOptions?.editingLayerId;
    const currentEditingLayer = allLayers.find((layer) => layer.ID === currentEditingLayerId);
    setIsEnabled(!currentEditingLayer?.disabledTools?.includes(toolName));
  }, [drawControl, toolName]);

  useEffect(() => {
    checkEnabled();

    const handleLayerChange = () => checkEnabled();
    const handleSelectionChange = () => checkEnabled();

    map?.on("draw.layerchange", handleLayerChange);
    map?.on("draw.selectionchange", handleSelectionChange);

    return () => {
      map?.off("draw.layerchange", handleLayerChange);
      map?.off("draw.selectionchange", handleSelectionChange);
    };
  }, [map, checkEnabled]);

  return isEnabled;
}
